import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { PhotoUploader } from "@/components/common/PhotoUploader";
import { usersService } from "@/services/users.service";
import { getErrorMessage } from "@/services/api";
import { ROLE_OPTIONS, STATUS_OPTIONS } from "@/config/constants";

function Field({ label, error, children, className = "" }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-sm font-medium">{label}</Label>
            {children}
            {error && <div className="text-xs text-danger">{error}</div>}
        </div>
    );
}

function createSchema(isEdit) {
    return z.object({
        name: z.string().min(2, "Informe o nome"),
        email: z.string().email("E-mail inválido"),
        phone: z.string().optional().nullable(),
        password: isEdit
            ? z.string().optional()
            : z.string().min(8, "Senha mínima de 8 caracteres"),
        role: z.string().min(1, "Escolha um perfil"),
        status: z.string().min(1),
        photo_url: z.string().optional().nullable(),
    });
}

export default function UserFormPage() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();

    const detail = useQuery({
        queryKey: ["user", id],
        queryFn: () => usersService.get(id),
        enabled: isEdit,
    });

    const schema = React.useMemo(() => createSchema(isEdit), [isEdit]);
    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            password: "",
            role: "Technician",
            status: "active",
            photo_url: "",
        },
    });

    React.useEffect(() => {
        if (isEdit && detail.data) {
            reset({
                name: detail.data.name || "",
                email: detail.data.email || "",
                phone: detail.data.phone || "",
                password: "",
                role: detail.data.role || "Technician",
                status: detail.data.status || "active",
                photo_url: detail.data.photo_url || "",
            });
        }
    }, [isEdit, detail.data, reset]);

    const save = useMutation({
        mutationFn: (data) => {
            const payload = { ...data };
            if (isEdit) {
                delete payload.password;
                delete payload.email; // email is immutable in this MVP
            }
            ["phone", "photo_url"].forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            return isEdit ? usersService.update(id, payload) : usersService.create(payload);
        },
        onSuccess: () => {
            toast.success(isEdit ? "Usuário atualizado." : "Usuário criado.");
            qc.invalidateQueries({ queryKey: ["users"] });
            navigate("/users");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar usuário.")),
    });

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-6">
            <PageHeader
                title={isEdit ? "Editar usuário" : "Novo usuário"}
                description="Defina credenciais, perfil de acesso e status."
                backTo="/users"
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Usuários", to: "/users" },
                    { label: isEdit ? "Editar" : "Novo" },
                ]}
                actions={
                    <>
                        <Button type="button" variant="ghost" onClick={() => navigate("/users")}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={save.isPending}
                            data-testid="user-save-button"
                        >
                            {save.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="mr-2 h-4 w-4" />
                            )}
                            Salvar
                        </Button>
                    </>
                }
            />

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Dados de acesso</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <Field label="Nome *" error={errors.name?.message} className="md:col-span-2">
                                <Input {...register("name")} data-testid="user-name" />
                            </Field>
                            <Field label="E-mail *" error={errors.email?.message}>
                                <Input
                                    type="email"
                                    {...register("email")}
                                    disabled={isEdit}
                                    data-testid="user-email"
                                />
                            </Field>
                            <Field label="Telefone">
                                <Input {...register("phone")} data-testid="user-phone" />
                            </Field>
                            {!isEdit && (
                                <Field label="Senha *" error={errors.password?.message} className="md:col-span-2">
                                    <Input
                                        type="password"
                                        {...register("password")}
                                        data-testid="user-password"
                                    />
                                </Field>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Perfil e status</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <Field label="Perfil *" error={errors.role?.message}>
                                <Select value={watch("role")} onValueChange={(v) => setValue("role", v)}>
                                    <SelectTrigger data-testid="user-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLE_OPTIONS.map((r) => (
                                            <SelectItem key={r.value} value={r.value}>
                                                {r.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Status">
                                <Select value={watch("status")} onValueChange={(v) => setValue("status", v)}>
                                    <SelectTrigger data-testid="user-status">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </CardContent>
                    </Card>
                </div>

                <div className="lg:col-span-1">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Foto</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <PhotoUploader
                                value={watch("photo_url")}
                                onChange={(v) => setValue("photo_url", v)}
                                testId="user-photo-uploader"
                            />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </form>
    );
}
